/**
 * Admin Users Page
 * Manage users, roles, and access control
 */

import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { getApiUrl } from '../../config/api';
import {
  UserIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

interface User {
  id: number;
  clerk_id: string;
  email: string;
  name: string | null;
  role: 'user' | 'admin';
  is_active: number;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UserStats {
  total_users: number;
  admin_users: number;
  active_users: number;
  new_users_30d: number;
  recent_logins: Array<{
    id: number;
    name: string | null;
    email: string;
    last_login_at: string;
  }>;
}

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
      });

      if (searchQuery) params.append('search', searchQuery);

      const [usersRes, statsRes] = await Promise.all([
        fetchWithAuth(getApiUrl(`api/v1/admin/users?${params}`)),
        fetchWithAuth(getApiUrl('api/v1/admin/users/stats')),
      ]);

      const usersData = await usersRes.json();
      const statsData = await statsRes.json();

      if (usersData.success) {
        setUsers(usersData.data);
        setTotalPages(usersData.pagination.total_pages);
      }
      if (statsData.success) {
        setStats(statsData.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage, searchQuery]);

  const toggleUserStatus = async (userId: number, currentStatus: number) => {
    try {
      const res = await fetchWithAuth(getApiUrl(`api/v1/admin/users/${userId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: currentStatus === 1 ? 0 : 1 }),
      });

      const data = await res.json();
      if (data.success) {
        loadData();
      }
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const toggleUserRole = async (userId: number, currentRole: string) => {
    try {
      const newRole = currentRole === 'admin' ? 'user' : 'admin';
      const res = await fetchWithAuth(getApiUrl(`api/v1/admin/users/${userId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });

      const data = await res.json();
      if (data.success) {
        loadData();
      }
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-100">Users</h1>
          <p className="text-gray-400 mt-1">Manage user accounts, roles, and permissions</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Total Users</div>
            <div className="text-2xl font-bold text-gray-100">{stats.total_users}</div>
          </div>
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
            <div className="text-sm text-purple-400 mb-1">Admins</div>
            <div className="text-2xl font-bold text-purple-400">{stats.admin_users}</div>
          </div>
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
            <div className="text-sm text-green-400 mb-1">Active Users</div>
            <div className="text-2xl font-bold text-green-400">{stats.active_users}</div>
          </div>
          <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
            <div className="text-sm text-cyan-400 mb-1">New (30 days)</div>
            <div className="text-2xl font-bold text-cyan-400">{stats.new_users_30d}</div>
          </div>
        </div>
      )}

      {/* Recent Logins & Users Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users Table */}
        <div className="lg:col-span-2 bg-gray-800/30 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-100">User Management</h3>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-900/50 border border-gray-700 rounded">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none text-sm text-gray-100 placeholder-gray-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="pb-2 font-medium">User</th>
                  <th className="pb-2 font-medium">Role</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Last Login</th>
                  <th className="pb-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-800/30">
                    <td className="py-3">
                      <div>
                        <div className="text-gray-200 font-medium">
                          {user.name || 'Unnamed User'}
                        </div>
                        <div className="text-xs text-gray-400">{user.email}</div>
                      </div>
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => toggleUserRole(user.id, user.role)}
                        className={`flex items-center gap-1 text-xs px-2 py-1 border rounded ${
                          user.role === 'admin'
                            ? 'bg-purple-900/20 border-purple-500/30 text-purple-400'
                            : 'bg-gray-700 border-gray-600 text-gray-300'
                        }`}
                      >
                        {user.role === 'admin' && <ShieldCheckIcon className="w-3 h-3" />}
                        {user.role}
                      </button>
                    </td>
                    <td className="py-3">
                      <span
                        className={`flex items-center gap-1 text-xs px-2 py-1 border rounded w-fit ${
                          user.is_active
                            ? 'text-green-400 bg-green-900/20 border-green-500/30'
                            : 'text-red-400 bg-red-900/20 border-red-500/30'
                        }`}
                      >
                        {user.is_active ? (
                          <CheckCircleIcon className="w-3 h-3" />
                        ) : (
                          <XCircleIcon className="w-3 h-3" />
                        )}
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 text-gray-400 text-xs">
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleString()
                        : 'Never'}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                        className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
            <span className="text-sm text-gray-400">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Recent Logins */}
        <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-100 mb-4">Recent Logins</h3>
          {stats && stats.recent_logins && stats.recent_logins.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_logins.map((login) => (
                <div
                  key={login.id}
                  className="bg-gray-900/30 border border-gray-700/50 rounded p-3"
                >
                  <div className="flex items-start gap-2">
                    <UserIcon className="w-4 h-4 text-cyber-blue-400 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-200 font-medium">
                        {login.name || 'Unnamed User'}
                      </div>
                      <div className="text-xs text-gray-400">{login.email}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(login.last_login_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No recent logins</p>
          )}
        </div>
      </div>
    </div>
  );
}
